import { ApiWrapper } from './api-wrapper.js';
import { ROUTE_CONTRACTS } from './routes.contract.js';
import type {
  ModelRouteParams,
  RenameRoundRouteParams,
  SessionPathRouteParams,
  SessionProductRouteParams,
  SessionProductVersionRouteParams,
  SessionRouteParams,
  SessionAttachedFileRouteParams,
  SessionTempUploadRouteParams,
  TeamMemberRouteParams,
  TeamRouteParams,
  UserRouteParams,
} from './routes.contract.js';

export function createApiRoutes(wrapper: ApiWrapper) {
  return {
    health: {
      get: wrapper.functionize(ROUTE_CONTRACTS.health.get),
    },
    sessions: {
      list: wrapper.functionize(ROUTE_CONTRACTS.sessions.list),
      count: wrapper.functionize(ROUTE_CONTRACTS.sessions.count),
      resourcesSummary: wrapper.functionize(ROUTE_CONTRACTS.sessions.resourcesSummary),
      create: wrapper.functionize(ROUTE_CONTRACTS.sessions.create),
      byId: (sessionId: SessionRouteParams['sessionId']) => ({
        get: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.get,
          defaultParams: { sessionId },
        }),
        initialLoad: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.initialLoad,
          defaultParams: { sessionId },
        }),
        previews: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.previews,
          defaultParams: { sessionId },
        }),
        overview: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.overview,
          defaultParams: { sessionId },
        }),
        productReads: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.productReads,
          defaultParams: { sessionId },
        }),
        team: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.team,
          defaultParams: { sessionId },
        }),
        missionBriefing: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.missionBriefing,
          defaultParams: { sessionId },
        }),
        teamMembers: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.teamMembers,
          defaultParams: { sessionId },
        }),
        update: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.update,
          defaultParams: { sessionId },
        }),
        delete: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.remove,
          defaultParams: { sessionId },
        }),
        intent: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.intent,
          defaultParams: { sessionId },
        }),
        tempUploads: {
          create: wrapper.functionize({
            ...ROUTE_CONTRACTS.sessions.tempUploadCreate,
            defaultParams: { sessionId },
          }),
          byId: (uploadId: SessionTempUploadRouteParams['uploadId']) => ({
            delete: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.tempUploadDelete,
              defaultParams: { sessionId, uploadId },
            }),
            signedUrl: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.tempUploadSignedUrl,
              defaultParams: { sessionId, uploadId },
            }),
          }),
        },
        attachedFiles: {
          byId: (attachedFileId: SessionAttachedFileRouteParams['attachedFileId']) => ({
            signedUrl: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.attachedFileSignedUrl,
              defaultParams: { sessionId, attachedFileId },
            }),
          }),
        },
        collabs: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.collabs,
          defaultParams: { sessionId },
        }),
        markChatRead: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.markChatRead,
          defaultParams: { sessionId },
        }),
        products: {
          list: wrapper.functionize({
            ...ROUTE_CONTRACTS.sessions.products,
            defaultParams: { sessionId },
          }),
          byId: (productId: SessionProductRouteParams['productId']) => ({
            get: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.product,
              defaultParams: { sessionId, productId },
            }),
            versions: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.versions,
              defaultParams: { sessionId, productId },
            }),
            versionById: (versionId: SessionProductVersionRouteParams['versionId']) =>
              wrapper.functionize({
                ...ROUTE_CONTRACTS.sessions.version,
                defaultParams: { sessionId, productId, versionId },
              }),
            markRead: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.markRead,
              defaultParams: { sessionId, productId },
            }),
            timeline: wrapper.functionize({
              ...ROUTE_CONTRACTS.sessions.timeline,
              defaultParams: { sessionId, productId },
            }),
          }),
        },
        paths: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.paths,
          defaultParams: { sessionId },
        }),
        travel: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.travel,
          defaultParams: { sessionId },
        }),
        clipFuture: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.clipFuture,
          defaultParams: { sessionId },
        }),
        renamePath: (pathId: SessionPathRouteParams['pathId']) =>
          wrapper.functionize({
            ...ROUTE_CONTRACTS.sessions.renamePath,
            defaultParams: { sessionId, pathId },
          }),
        renameRound: (round: RenameRoundRouteParams['round']) =>
          wrapper.functionize({
            ...ROUTE_CONTRACTS.sessions.renameRound,
            defaultParams: { sessionId, round },
          }),
        halt: wrapper.functionize({
          ...ROUTE_CONTRACTS.sessions.halt,
          defaultParams: { sessionId },
        }),
      }),
    },
    teams: {
      list: wrapper.functionize(ROUTE_CONTRACTS.teams.list),
      listPreview: wrapper.functionize(ROUTE_CONTRACTS.teams.listPreview),
      create: wrapper.functionize(ROUTE_CONTRACTS.teams.create),
      createFromSession: wrapper.functionize(ROUTE_CONTRACTS.teams.createFromSession),
      byId: (teamId: TeamRouteParams['teamId']) => ({
        get: wrapper.functionize({
          ...ROUTE_CONTRACTS.teams.get,
          defaultParams: { teamId },
        }),
        replicate: wrapper.functionize({
          ...ROUTE_CONTRACTS.teams.replicate,
          defaultParams: { teamId },
        }),
        update: wrapper.functionize({
          ...ROUTE_CONTRACTS.teams.update,
          defaultParams: { teamId },
        }),
        setModelMode: wrapper.functionize({
          ...ROUTE_CONTRACTS.teams.setModelMode,
          defaultParams: { teamId },
        }),
        delete: wrapper.functionize({
          ...ROUTE_CONTRACTS.teams.remove,
          defaultParams: { teamId },
        }),
        members: {
          add: wrapper.functionize({
            ...ROUTE_CONTRACTS.teams.addMember,
            defaultParams: { teamId },
          }),
          byId: (memberId: TeamMemberRouteParams['memberId']) => ({
            update: wrapper.functionize({
              ...ROUTE_CONTRACTS.teams.updateMember,
              defaultParams: { teamId, memberId },
            }),
            delete: wrapper.functionize({
              ...ROUTE_CONTRACTS.teams.removeMember,
              defaultParams: { teamId, memberId },
            }),
          }),
        },
      }),
    },
    tags: {
      list: wrapper.functionize(ROUTE_CONTRACTS.tags.list),
    },
    profile: {
      get: wrapper.functionize(ROUTE_CONTRACTS.profile.get),
      update: wrapper.functionize(ROUTE_CONTRACTS.profile.update),
      uploadImage: wrapper.functionize(ROUTE_CONTRACTS.profile.uploadImage),
      deleteImage: wrapper.functionize(ROUTE_CONTRACTS.profile.deleteImage),
    },
    users: {
      byId: (userId: UserRouteParams['userId']) => ({
        readPublic: wrapper.functionize({
          ...ROUTE_CONTRACTS.users.readPublic,
          defaultParams: { userId },
        }),
      }),
    },
    geo: {
      resolve: wrapper.functionize(ROUTE_CONTRACTS.geo.resolve),
    },
    models: {
      list: wrapper.functionize(ROUTE_CONTRACTS.models.list),
      byId: (id: ModelRouteParams['id']) =>
        wrapper.functionize({
          ...ROUTE_CONTRACTS.models.get,
          defaultParams: { id },
        }),
    },
    subscription: {
      plans: wrapper.functionize(ROUTE_CONTRACTS.subscription.plans),
      current: wrapper.functionize(ROUTE_CONTRACTS.subscription.current),
      usage: wrapper.functionize(ROUTE_CONTRACTS.subscription.usage),
      checkout: wrapper.functionize(ROUTE_CONTRACTS.subscription.checkout),
      changePlan: wrapper.functionize(ROUTE_CONTRACTS.subscription.changePlan),
      cancel: wrapper.functionize(ROUTE_CONTRACTS.subscription.cancel),
      resume: wrapper.functionize(ROUTE_CONTRACTS.subscription.resume),
      portal: wrapper.functionize(ROUTE_CONTRACTS.subscription.portal),
    },
    system: {
      reachOut: wrapper.functionize(ROUTE_CONTRACTS.system.reachOut),
    },
  };
}

export type ApiRoutes = ReturnType<typeof createApiRoutes>;
